<script setup lang="ts">
import { __ } from '@@/bits/intl'
import OzBaseButton from '@@/library/v4/components/OzBaseButton.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import OzTooltip from '@@/library/v4/components/OzTooltip.vue'
import Teleport from '@@/vuecomponents/Teleport.vue'
import { offset, shift } from '@floating-ui/vue'
import { computed, ref, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    usageContext: SurfaceTranslationIndicatorUsageContext
    isDarkMode?: boolean | 'auto'
    size?: number
    isTranslating?: boolean
  }>(),
  {
    isDarkMode: 'auto',
    size: 12,
    isTranslating: false,
  },
)

const globeIconRef = ref<InstanceType<typeof OzIcon> | null>(null)
const globeIconEl = computed(() => globeIconRef.value?.$el)
const xTooltip = ref<boolean>(false)
const xCheckmark = ref<boolean>(false)
const checkmarkTimeout = ref<ReturnType<typeof setTimeout> | null>(null)

const tooltipText = computed(() => {
  if (props.isTranslating) {
    return __('Translating...')
  }
  return props.usageContext === 'post' ? __('Post translated') : __('Comment translated')
})

const iconBaseClasses = computed(() => ({
  'text-dark-text-300': props.isDarkMode === false,
  'text-light-text-300': props.isDarkMode === true,
  'text-dark-text-300 dark:text-light-text-300': props.isDarkMode === 'auto',
}))

watch(
  () => props.isTranslating,
  (newValue, oldValue) => {
    if (checkmarkTimeout.value) {
      clearTimeout(checkmarkTimeout.value)
      checkmarkTimeout.value = null
    }

    if (newValue === false && oldValue === true) {
      // When translation is completed
      xCheckmark.value = true
      checkmarkTimeout.value = setTimeout(() => {
        xCheckmark.value = false
      }, 1500)
    } else {
      xCheckmark.value = false
    }
  },
)
</script>

<script lang="ts">
enum SurfaceTranslationIndicatorUsageContext {
  Post = 'post',
  Comment = 'comment',
}

export { SurfaceTranslationIndicatorUsageContext }
</script>

<template>
  <div>
    <Teleport to="#surface-panels">
      <OzTooltip
        v-show="globeIconEl && xTooltip"
        :anchor="globeIconEl"
        :class="'z-modal2'"
        :config="{
          placement: props.usageContext === 'post' ? 'bottom' : 'top',
          middleware: [
            offset({
              // Move the popover up by 8px so it doesn't obstruct the tooltip button.
              mainAxis: 8,
            }),
            shift(),
          ],
        }"
        :dark-mode="isDarkMode"
        class="max-w-xs whitespace-nowrap"
      >
        <p id="globe-icon-tooltip">{{ tooltipText }}</p>
      </OzTooltip>
    </Teleport>

    <OzBaseButton
      ref="globeIconRef"
      class="bg-transparent items-start p-0 block"
      aria-describedby="globe-icon-tooltip"
      @focus="xTooltip = true"
      @blur="xTooltip = false"
      @mouseenter="xTooltip = true"
      @mouseleave="xTooltip = false"
      @click="xTooltip = !xTooltip"
    >
      <!-- Checkmark -->
      <svg
        v-if="xCheckmark"
        :width="props.size"
        :height="props.size"
        viewBox="0 0 12 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        :class="['checkmark-icon', iconBaseClasses]"
      >
        <path
          d="M2 6.75L4.25 9L9.75 3.5"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
      <OzIcon
        v-else
        name="globe"
        :class="[iconBaseClasses, props.isTranslating && 'animate-rotate-globe']"
        :size="props.size"
      />
    </OzBaseButton>
  </div>
</template>

<style scoped>
@keyframes rotate-globe {
  from {
    transform: rotateY(0deg);
  }
  to {
    transform: rotateY(360deg);
  }
}

.animate-rotate-globe {
  animation: rotate-globe 1s linear infinite;
}

.checkmark-icon {
  @apply origin-center stroke-[1.5px];

  stroke-dasharray: 24;
  stroke-dashoffset: 24;
  stroke-linecap: round;
  animation: stroke 0.3s cubic-bezier(0.65, 0, 0.45, 1) 0.1s forwards;
}

@keyframes stroke {
  0% {
    stroke-dashoffset: 24px;
  }
  100% {
    stroke-dashoffset: 0;
  }
}
</style>
